"use client";

import Image from "next/image";
import Link from "next/link";
import { useLocale, useTranslations } from "next-intl";
import { FEATURED_PROJECTS } from "./works/works-data";

/**
 * Portfolio teaser on the home page. The full grid lives on /works; this shows
 * the first projects so visitors see real work without leaving the landing page.
 */
export default function WorksPreviewSection() {
  const t = useTranslations("Works");
  const tHome = useTranslations("Home");
  const locale = useLocale();
  const isArabic = locale === "ar";
  const copy = t.raw("projects") as { title: string; description: string; tags: string[] }[];
  const featured = FEATURED_PROJECTS;

  return (
    <section id="works" dir={isArabic ? "rtl" : "ltr"} className="relative px-4 py-14 md:px-6 lg:px-8 lg:py-20">
      <div className="mx-auto flex w-full max-w-310 flex-col items-center gap-12">
        <header className="flex max-w-170 flex-col items-center gap-3 text-center">
          <h2 className="m-0 bg-[linear-gradient(89.18deg,#0CA5F0_15.53%,#FFFFFF_220.84%)] bg-clip-text text-[28px] font-black leading-[1.35] text-transparent lg:text-[36px]">
            {tHome("worksTitle")}
          </h2>
          <p className="m-0 text-[16px] leading-[1.9] text-white/70">{tHome("worksSubtitle")}</p>
        </header>

        {/* data-nosnippet: these cards repeat each project page's own summary,
            and Google was stitching them into the home page's search snippet
            in place of the site description. The heading above stays eligible. */}
        <div
          data-nosnippet
          className="grid w-full grid-cols-1 gap-8 sm:grid-cols-2 lg:grid-cols-3"
        >
          {featured.map((project) => {
            const entry = copy[project.index];

            return (
              <Link
                key={project.index}
                href={`/${locale}${project.href}`}
                className="group flex flex-col overflow-hidden rounded-3xl border border-sky-500/25 bg-[#13192B] transition-colors duration-300 hover:border-sky-500/70 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#0CA5F0]"
              >
                <div className="relative aspect-[3/2] w-full overflow-hidden bg-[#0b1220]">
                  {project.image ? (
                    <Image
                      src={project.image}
                      alt={entry.title}
                      fill
                      sizes="(max-width: 640px) 92vw, (max-width: 1024px) 45vw, 380px"
                      className="object-cover transition-transform duration-500 group-hover:scale-105"
                    />
                  ) : null}
                  <span className="absolute end-3 top-3 inline-flex items-center rounded-full bg-cyan-600 px-3 py-1 text-[13px] font-bold text-white">
                    {t(`filters.${project.category}`)}
                  </span>
                </div>
                <div className={`flex flex-1 flex-col gap-2 p-6 ${isArabic ? "text-right" : "text-left"}`}>
                  <h3 className="m-0 text-[18px] font-bold leading-[1.7] text-white">{entry.title}</h3>
                  <p className="m-0 line-clamp-2 text-[14px] leading-[1.85] text-white/65">{entry.description}</p>
                  <span className="mt-auto pt-2 text-[14px] font-bold text-cyan-400">{t("viewDetails")}</span>
                </div>
              </Link>
            );
          })}
        </div>

        <Link
          href={`/${locale}/works`}
          className="inline-flex min-h-12 items-center justify-center rounded-full border border-sky-500/40 px-7 text-[16px] font-bold text-white transition-colors duration-300 hover:bg-white hover:text-[#13192B] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#0CA5F0]"
        >
          {tHome("worksCta")}
        </Link>
      </div>
    </section>
  );
}
